// Utility functions for grape variety handling

// Normalize grape variety names for URL slugs
export const normalizeGrapeVarietyName = (name) => {
  if (!name) return '';
  
  return name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[éèê]/g, 'e')
    .replace(/[àâ]/g, 'a')
    .replace(/[ùû]/g, 'u')
    .replace(/[ôö]/g, 'o')
    .replace(/[îï]/g, 'i')
    .replace(/[ç]/g, 'c')
    .replace(/[^a-z0-9-]/g, '');
};

// Normalize region names for URL slugs
export const normalizeRegionName = (name) => {
  if (!name) return '';
  
  return name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[éèê]/g, 'e')
    .replace(/[àâ]/g, 'a')
    .replace(/[ùû]/g, 'u')
    .replace(/[ôö]/g, 'o')
    .replace(/[îï]/g, 'i')
    .replace(/[ç]/g, 'c')
    .replace(/[^a-z0-9-]/g, '');
};

// Map country names to country slugs
export const getCountrySlug = (country) => {
  if (!country) return 'france'; // Default to France
  
  const countryLower = country.toLowerCase();
  
  const countryMap = {
    'france': 'france',
    'italy': 'italy',
    'italie': 'italy',
    'spain': 'spain',
    'espagne': 'spain',
    'portugal': 'portugal',
    'argentina': 'argentina',
    'argentine': 'argentina',
    'chile': 'chile',
    'chili': 'chile',
    'australia': 'australia',
    'australie': 'australia',
    'austria': 'austria',
    'autriche': 'austria',
    'switzerland': 'switzerland',
    'suisse': 'switzerland',
    'mexico': 'mexico',
    'mexique': 'mexico',
    'germany': 'germany',
    'allemagne': 'germany',
    'new zealand': 'new-zealand',
    'nouvelle-zélande': 'new-zealand',
    'california': 'california',
    'californie': 'california',
    'south africa': 'south-africa',
    'afrique du sud': 'south-africa'
  };
  
  return countryMap[countryLower] || 'france';
};

// Map region names to region slugs
export const getRegionSlug = (region) => {
  if (!region) return 'bordeaux'; // Default to Bordeaux
  
  const regionLower = region.toLowerCase();
  
  const regionMap = {
    // France
    'bordeaux': 'bordeaux',
    'provence': 'provence',
    'bourgogne': 'bourgogne',
    'champagne': 'champagne',
    'loire': 'loire',
    'rhone': 'rhone',
    'alsace': 'alsace',
    'jura': 'jura',
    'languedoc-roussillon': 'languedoc-roussillon',
    'sud-ouest': 'sud-ouest',
    
    // Spain
    'rioja': 'rioja',
    'ribera del duero': 'ribera-del-duero',
    'ribera-del-duero': 'ribera-del-duero',
    'navarra': 'navarra',
    'priorat': 'priorat',
    'rueda': 'rueda',
    'rias baixas': 'rias-baixas',
    'rias-baixas': 'rias-baixas',
    'jerez': 'jerez',
    'cava': 'cava',
    
    // Portugal
    'douro': 'douro',
    'alentejo': 'alentejo',
    'dao': 'dao',
    'vinho verde': 'vinho-verde',
    'vinho-verde': 'vinho-verde',
    'bairrada': 'bairrada',
    'lisboa': 'lisboa',
    'setubal': 'setubal',
    'madeira': 'madeira',
    
    // Italy
    'toscane': 'toscane',
    'piemont': 'piemont',
    'venetie': 'venetie',
    'sicile': 'sicile',
    'supertuscan': 'supertuscan',
    'pouilles': 'pouilles',
    'emilie-romagne': 'emilie-romagne',
    'lombardie': 'lombardie',
    'trentin-haut-adige': 'trentin-haut-adige',
    'frioul-venetie-julienne': 'frioul-venetie-julienne',
    'abruzzes': 'abruzzes',
    'marches': 'marches',
    'campanie': 'campanie',
    
    // Austria
    'niederösterreich': 'niederoesterreich',
    'niederoesterreich': 'niederoesterreich',
    'burgenland': 'burgenland',
    'steiermark': 'steiermark',
    'wien': 'wien',
    
    // Switzerland
    'valais': 'valais',
    'vaud': 'vaud',
    
    // Germany
    'rheinhessen': 'rheinhessen',
    'pfalz': 'pfalz',
    'mosel': 'mosel',
    
    // Argentina
    'mendoza': 'mendoza',
    'patagonie': 'patagonie',
    'salta': 'salta',
    
    // Chile
    'maipo': 'maipo',
    'colchagua': 'colchagua',
    
    // Australia
    'barossa valley': 'barossa-valley',
    'barossa-valley': 'barossa-valley',
    'margaret river': 'margaret-river',
    'margaret-river': 'margaret-river',
    'hunter valley': 'hunter-valley',
    'hunter-valley': 'hunter-valley',
    'mclaren vale': 'mclaren-vale',
    'mclaren-vale': 'mclaren-vale',
    'yarra valley': 'yarra-valley',
    'yarra-valley': 'yarra-valley',
    'clare valley': 'clare-valley',
    'clare-valley': 'clare-valley',
    'coonawarra': 'coonawarra',
    'adelaide hills': 'adelaide-hills',
    'adelaide-hills': 'adelaide-hills',
    'mornington peninsula': 'mornington-peninsula',
    'mornington-peninsula': 'mornington-peninsula',
    'tasmanie': 'tasmanie',
    'autres australie': 'autres-australie',
    'autres-australie': 'autres-australie',
    
    // New Zealand
    'marlborough': 'marlborough',
    'hawkes bay': 'hawkes-bay',
    'hawkes-bay': 'hawkes-bay',
    'central otago': 'central-otago',
    'central-otago': 'central-otago',
    'wairarapa': 'wairarapa',
    'canterbury': 'canterbury',
    'nelson': 'nelson',
    
    // Mexico
    'baja california': 'baja-california',
    'baja-california': 'baja-california',
    'coahuila': 'coahuila',
    'queretaro': 'queretaro',
    'autres': 'autres',
    
    // Greece
    'crete': 'crete',
    'macedonia': 'macedonia',
    'peloponnese': 'peloponnese'
  };
  
  return regionMap[regionLower] || normalizeRegionName(region);
};

// Get grape variety info URL
export const getGrapeVarietyInfoUrl = (grapeVariety, country, region = null, subregion = null) => {
  const normalizedGrape = normalizeGrapeVarietyName(grapeVariety);
  const countrySlug = getCountrySlug(country);
  
  if (region && subregion) {
    const regionSlug = getRegionSlug(region);
    const subregionSlug = subregion; // Use subregion directly, don't normalize as grape variety
    return `/info/countries/${countrySlug}/regions/${regionSlug}/${subregionSlug}/cepages/${normalizedGrape}`;
  } else if (region) {
    const regionSlug = getRegionSlug(region);
    return `/info/countries/${countrySlug}/regions/${regionSlug}/cepages/${normalizedGrape}`;
  } else {
    return `/info/countries/${countrySlug}/cepages/${normalizedGrape}`;
  }
};

// Get region info URL
export const getRegionInfoUrl = (region, country = 'france') => {
  if (!region) return null;
  
  const regionSlug = getRegionSlug(region);
  const countrySlug = getCountrySlug(country);
  
  return `/info/countries/${countrySlug}/regions/${regionSlug}`;
};

// Get appellation/subregion info URL
export const getAppellationInfoUrl = (appellation, region, country = 'france') => {
  if (!appellation) return null;
  
  const appellationLower = appellation.toLowerCase();
  const regionLower = region?.toLowerCase() || '';
  const countrySlug = getCountrySlug(country);
  
  // Map appellation names to slugs
  const appellationMap = {
    // France
    'bandol': 'bandol',
    'côtes de provence': 'cotes-provence',
    'cotes de provence': 'cotes-provence',
    'cassis': 'cassis',
    'châteauneuf-du-pape': 'chateauneuf-du-pape',
    'chateauneuf du pape': 'chateauneuf-du-pape',
    'côte-rôtie': 'cote-rotie',
    'cote rotie': 'cote-rotie',
    'hermitage': 'hermitage',
    'sancerre': 'sancerre',
    'pouilly-fumé': 'pouilly-fume',
    'pouilly fume': 'pouilly-fume',
    'graves': 'graves',
    'pauillac': 'pauillac',
    'margaux': 'margaux',
    'saint-julien': 'saint-julien',
    'saint-estèphe': 'saint-estephe',
    'saint-estephe': 'saint-estephe',
    'pomerol': 'pomerol',
    'saint-émilion': 'saint-emilion',
    'saint-emilion': 'saint-emilion',
    'sauternes': 'sauternes',
    'barsac': 'barsac',
    'listrac-médoc': 'listrac-medoc',
    'listrac medoc': 'listrac-medoc',
    'moulis-en-médoc': 'moulis-en-medoc',
    'moulis en medoc': 'moulis-en-medoc',
    'arbois': 'arbois',
    'château-chalon': 'chateau-chalon',
    'chateau chalon': 'chateau-chalon',
    'l\'étoile': 'l-etoile',
    'l\'etoile': 'l-etoile',
    'côtes du jura': 'cotes-du-jura',
    'cotes du jura': 'cotes-du-jura',
    
    // Spain
    'rioja alta': 'rioja-alta',
    'rioja alavesa': 'rioja-alavesa',
    'rioja baja': 'rioja-baja',
    'ribera alta': 'ribera-alta',
    'ribera media': 'ribera-media',
    'ribera baja': 'ribera-baja',
    'tierra estella': 'tierra-estella',
    'valdizarbe': 'valdizarbe',
    'baja montaña': 'baja-montana',
    'baja montana': 'baja-montana',
    
    // Portugal
    'cima corgo': 'cima-corgo',
    'alentejo central': 'alentejo-central',
    'serra da estrela': 'serra-da-estrela',
    'moncao e melgaco': 'moncao-melgaco',
    'moncao-melgaco': 'moncao-melgaco',
    
    // Italy
    'chianti classico': 'chianti-classico',
    'langhe': 'langhe',
    'valpolicella': 'valpolicella',
    'bolgheri': 'bolgheri',
    'maremma': 'maremma',
    
    // Austria
    'südsteiermark': 'sudsteiermark',
    'sudsteiermark': 'sudsteiermark',
    'weststeiermark': 'weststeiermark',
    'oststeiermark': 'oststeiermark',
    'nussberg': 'nussberg',
    'bisamberg': 'bisamberg',
    'mauer': 'mauer',
    
    // Australia
    'eden valley': 'eden-valley',
    'high eden': 'high-eden',
    'lower hunter valley': 'lower-hunter-valley',
    'wilyabrup': 'wilyabrup',
    'blewitt springs': 'blewitt-springs',
    'upper yarra valley': 'upper-yarra-valley',
    'clare': 'clare',
    'penola': 'penola',
    'lenswood': 'lenswood',
    
    // New Zealand
    'wairau valley': 'wairau-valley',
    'gimblett gravels': 'gimblett-gravels',
    'bannockburn': 'bannockburn',
    'martinborough': 'martinborough',
    'gladstone': 'gladstone',
    'masterton': 'masterton',
    'waipara valley': 'waipara-valley',
    'north canterbury': 'north-canterbury',
    'central canterbury': 'central-canterbury',
    'moutere hills': 'moutere-hills',
    'waimea plains': 'waimea-plains',
    'tasman bay': 'tasman-bay'
  };
  
  const appellationSlug = appellationMap[appellationLower] || normalizeRegionName(appellation);
  const regionSlug = getRegionSlug(regionLower);
  
  return `/info/countries/${countrySlug}/regions/${regionSlug}/${appellationSlug}`;
};

// Get country info URL
export const getCountryInfoUrl = (country) => {
  if (!country) return null;
  
  const countrySlug = getCountrySlug(country);
  return `/info/countries/${countrySlug}`;
};

// Common grape variety names mapping
export const grapeVarietyMapping = {
  // International varieties
  'cabernet sauvignon': 'cabernet-sauvignon',
  'cabernet-sauvignon': 'cabernet-sauvignon',
  'merlot': 'merlot',
  'pinot noir': 'pinot-noir',
  'pinot-noir': 'pinot-noir',
  'chardonnay': 'chardonnay',
  'sauvignon blanc': 'sauvignon-blanc',
  'sauvignon-blanc': 'sauvignon-blanc',
  'syrah': 'syrah',
  'shiraz': 'syrah',
  'malbec': 'malbec',
  'tempranillo': 'tempranillo',
  'sangiovese': 'sangiovese',
  'nebbiolo': 'nebbiolo',
  'pinot gris': 'pinot-gris',
  'pinot-gris': 'pinot-gris',
  'riesling': 'riesling',
  'gewürztraminer': 'gewurztraminer',
  'gewurztraminer': 'gewurztraminer',
  'viognier': 'viognier',
  'grenache': 'grenache',
  'mourvèdre': 'mourvedre',
  'mourvedre': 'mourvedre',
  'petit verdot': 'petit-verdot',
  'petit-verdot': 'petit-verdot',
  'carmenère': 'carmenere',
  'carmenere': 'carmenere',
  
  // Portuguese varieties
  'touriga nacional': 'touriga-nacional',
  'touriga-nacional': 'touriga-nacional',
  'alvarinho': 'alvarinho',
  'baga': 'baga',
  'encruzado': 'encruzado',
  
  // Italian varieties
  'pinot grigio': 'pinot-grigio',
  'pinot-grigio': 'pinot-grigio',
  'nero davola': 'nero-davola',
  'nero-davola': 'nero-davola',
  'cabernet franc': 'cabernet-franc',
  'cabernet-franc': 'cabernet-franc',
  
  // French varieties
  'poulsard': 'poulsard',
  'trousseau': 'trousseau',
  'savagnin': 'savagnin',
  
  // Austrian varieties
  'grüner veltliner': 'gruner-veltliner',
  'gruner veltliner': 'gruner-veltliner',
  'gruner-veltliner': 'gruner-veltliner',
  'zweigelt': 'zweigelt',
  'blaufränkisch': 'blaufrankisch',
  'blaufrankisch': 'blaufrankisch',
  
  // Spanish varieties
  'albariño': 'albariño',
  'albariño': 'albariño',
  'garnacha': 'garnacha',
  'graciano': 'graciano',
  'verdejo': 'verdejo',
  
  // Australian varieties
  'shiraz': 'shiraz',
  
  // New Zealand varieties
  'sauvignon blanc': 'sauvignon-blanc',
  'pinot noir': 'pinot-noir'
}; 